import { useState, useEffect, useCallback, useRef } from 'react';
import type { Flashcard, StudySettings, StudyResult } from '../types';
import FlashcardFlip from './FlashcardFlip';
import { ProgressTracker } from './ProgressTracker';
import { Button } from './ui/Button';
import {
  ThumbsUp,
  ThumbsDown,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  Volume2,
  Star,
  Timer,
  Play,
  Pause,
} from 'lucide-react';

interface StudyModeProps {
  cards: Flashcard[];
  settings?: Partial<StudySettings>;
  onComplete?: (results: StudyResult[]) => void;
  onExit?: () => void;
  className?: string;
}

export const StudyMode: React.FC<StudyModeProps> = ({
  cards,
  settings = {},
  onComplete,
  onExit,
  className = '',
}) => {
  const [studyCards, setStudyCards] = useState<Flashcard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [results, setResults] = useState<StudyResult[]>([]);
  const [_sessionStartTime] = useState(Date.now());
  const [isFinished, setIsFinished] = useState(false);
  const [cardStartTime, setCardStartTime] = useState(Date.now());

  const defaultSettings: StudySettings = {
    shuffle: true,
    showHints: true,
    autoAdvance: false,
    trackProgress: true,
    studyNewCards: true,
    studyReviewCards: true,
    cardsPerSession: 20,
    autoFlipEnabled: false,
    autoFlipDelay: 5,
    autoProgressEnabled: false,
    autoProgressDelay: 10,
  };

  const studySettings = { ...defaultSettings, ...settings };

  const [isAutoFlipping, setIsAutoFlipping] = useState(false);
  const [isAutoProgressing, setIsAutoProgressing] = useState(false);
  const autoFlipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoProgressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const clearAllTimers = useCallback(() => {
    if (autoFlipTimerRef.current) {
      clearTimeout(autoFlipTimerRef.current);
      autoFlipTimerRef.current = null;
    }
    if (autoProgressTimerRef.current) {
      clearTimeout(autoProgressTimerRef.current);
      autoProgressTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setIsAutoFlipping(false);
    setIsAutoProgressing(false);
    setTimeRemaining(null);
  }, []);

  const startAutoFlip = useCallback(() => {
    if (!studySettings.autoFlipEnabled || isFinished) return;
    clearAllTimers();
    setIsAutoFlipping(true);
    let remaining = studySettings.autoFlipDelay;
    setTimeRemaining(remaining);
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setTimeRemaining(remaining);
      if (remaining <= 0 && countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }, 1000);
    autoFlipTimerRef.current = setTimeout(() => {
      setShowAnswer(true);
      setIsAutoFlipping(false);
      setTimeRemaining(null);
      if (studySettings.autoProgressEnabled) {
        startAutoProgress();
      }
    }, studySettings.autoFlipDelay * 1000);
  }, [studySettings.autoFlipEnabled, studySettings.autoFlipDelay, studySettings.autoProgressEnabled, isFinished, clearAllTimers]);

  const startAutoProgress = useCallback(() => {
    if (!studySettings.autoProgressEnabled || isFinished) return;
    clearAllTimers();
    setIsAutoProgressing(true);
    let remaining = studySettings.autoProgressDelay;
    setTimeRemaining(remaining);
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setTimeRemaining(remaining);
      if (remaining <= 0 && countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }, 1000);
    autoProgressTimerRef.current = setTimeout(() => {
      setIsAutoProgressing(false);
      setTimeRemaining(null);
      if (currentIndex + 1 >= studyCards.length) {
        setIsFinished(true);
        onComplete?.(results);
      } else {
        setCurrentIndex((prev) => prev + 1);
        setShowAnswer(false);
        setCardStartTime(Date.now());
      }
    }, studySettings.autoProgressDelay * 1000);
  }, [studySettings.autoProgressEnabled, studySettings.autoProgressDelay, isFinished, clearAllTimers, currentIndex, studyCards.length, results, onComplete]);

  useEffect(() => {
    return () => clearAllTimers();
  }, [clearAllTimers]);

  useEffect(() => {
    if (!isFinished && studySettings.autoFlipEnabled && !showAnswer && !isAutoFlipping && !isAutoProgressing) {
      const timer = setTimeout(() => {
        startAutoFlip();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, studySettings.autoFlipEnabled, startAutoFlip, isFinished, showAnswer, isAutoFlipping, isAutoProgressing]);

  const handleQualityRating = useCallback((quality: number) => {
    const timeSpent = Date.now() - cardStartTime;
    const card = studyCards[currentIndex];

    const result: StudyResult = {
      cardId: card.id,
      quality,
      timeSpent,
      timestamp: new Date().toISOString(),
    };

    const newResults = [...results, result];
    setResults(newResults);
    clearAllTimers();

    if (currentIndex + 1 >= studyCards.length) {
      setIsFinished(true);
      onComplete?.(newResults);
    } else {
      setCurrentIndex((prev) => prev + 1);
      setShowAnswer(false);
      setCardStartTime(Date.now());
    }
  }, [cardStartTime, currentIndex, studyCards, results, clearAllTimers, onComplete]);

  useEffect(() => {
    let preparedCards = [...cards];
    if (studySettings.shuffle) {
      preparedCards = preparedCards.sort(() => Math.random() - 0.5);
    }
    preparedCards = preparedCards.slice(0, studySettings.cardsPerSession);
    setStudyCards(preparedCards);
    setCurrentIndex(0);
    setResults([]);
    setIsFinished(false);
    setShowAnswer(false);
    setCardStartTime(Date.now());
    clearAllTimers();
  }, [cards, studySettings.shuffle, studySettings.cardsPerSession, clearAllTimers]);

  const handleSkip = () => {
    clearAllTimers();
    if (currentIndex + 1 >= studyCards.length) {
      setIsFinished(true);
      onComplete?.(results);
    } else {
      setCurrentIndex((prev) => prev + 1);
      setShowAnswer(false);
      setCardStartTime(Date.now());
    }
  };

  const handleSpeak = () => {
    const card = studyCards[currentIndex];
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(
        showAnswer ? card.back : card.front
      );
      window.speechSynthesis.speak(utterance);
    }
  };

  if (studyCards.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">No cards available to study.</p>
        <Button onClick={onExit} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  if (isFinished) {
    const correct = results.filter((r) => r.quality >= 3).length;
    const total = results.length;
    const _accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

    return (
      <div className={`space-y-8 ${className}`}>
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <Star className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Session Complete!</h2>
          <p className="text-gray-600 dark:text-gray-400">You reviewed {total} cards</p>
        </div>
        <ProgressTracker results={results} totalCards={studyCards.length} />
        <div className="flex justify-center gap-3">
          <Button onClick={onExit} variant="primary">
            Back to Decks
          </Button>
        </div>
      </div>
    );
  }

  const currentCard = studyCards[currentIndex];
  const progress = studyCards.length > 0 ? ((currentIndex + 1) / studyCards.length) * 100 : 0;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>
            Card {currentIndex + 1} of {studyCards.length}
          </span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Timer Display */}
      {(isAutoFlipping || isAutoProgressing) && timeRemaining !== null && (
        <div className="flex items-center justify-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
          <Timer className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            {isAutoFlipping && `Flipping in ${timeRemaining}s`}
            {isAutoProgressing && `Advancing in ${timeRemaining}s`}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllTimers}
            className="text-blue-600 dark:text-blue-400"
          >
            <Pause className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Flashcard */}
      <div className="flex justify-center">
        <div className="w-full max-w-lg">
          <FlashcardFlip
            card={currentCard}
            onFlip={(flipped) => {
              setShowAnswer(flipped);
              if (flipped) clearAllTimers();
            }}
            showHint={studySettings.showHints}
            isFlipped={showAnswer}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSpeak}
          title="Read aloud"
        >
          <Volume2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={handleSkip} title="Skip card">
          <SkipForward className="w-4 h-4" />
        </Button>

        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

        {/* Difficulty rating */}
        <div className="flex items-center gap-2">
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleQualityRating(0)}
            title="Forgot"
          >
            <ThumbsDown className="w-4 h-4" />
            Forgot
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleQualityRating(3)}
            title="Hard"
          >
            Hard
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={() => handleQualityRating(5)}
            title="Easy"
          >
            <ThumbsUp className="w-4 h-4" />
            Easy
          </Button>
        </div>

        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setShowAnswer(false);
            setCurrentIndex(Math.max(0, currentIndex - 1));
          }}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setShowAnswer(false);
            setCurrentIndex(Math.min(studyCards.length - 1, currentIndex + 1));
          }}
          disabled={currentIndex === studyCards.length - 1}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Exit */}
      <div className="flex justify-center">
        <Button variant="ghost" onClick={onExit} className="text-sm text-gray-500">
          Exit Session
        </Button>
      </div>
    </div>
  );
};

export default StudyMode;
